/**
 * Platform-aware file download utilities
 * Handles different download behaviors across iOS, Android, and Desktop
 */

import { trackFileDownload } from './analyticsUtils';

/**
 * Detect user's platform
 */
export const detectPlatform = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // iOS detection
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios';
  }

  // Android detection
  if (/android/i.test(userAgent)) {
    return 'android';
  }

  // Desktop
  return 'desktop';
};

/**
 * Download file with platform-specific handling and progress tracking
 * @param {string} url - File URL to download
 * @param {string} filename - Suggested filename
 * @param {string} fileType - Type of file (video, document, presentation)
 * @param {Function} onProgress - Optional callback for progress updates (percent)
 */
export const downloadFile = async (url, filename, fileType = 'file', onProgress = null) => {
  const platform = detectPlatform();

  // Track the download event
  trackFileDownload(filename, fileType, url);

  try {
    if (platform === 'ios') {
      // iOS: Open in new window (Safari will show share sheet)
      // This is the most reliable method for iOS
      window.open(url, '_blank', 'noopener,noreferrer');
      return {
        success: true,
        platform: 'ios',
        message: 'File opened. Tap the Share icon in the top right, then select "Save to Files"'
      };
    } else {
      // Android & Desktop: Download with progress tracking
      return await downloadWithProgress(url, filename, onProgress);
    }
  } catch (error) {
    console.error('Download error:', error);

    // Fallback: just open the URL
    window.open(url, '_blank', 'noopener,noreferrer');

    return {
      success: false,
      platform: platform,
      message: 'File opened in new window. Use browser options to save.',
      error: error.message
    };
  }
};

/**
 * Download file using fetch API with progress tracking
 * @param {string} url - File URL
 * @param {string} filename - Suggested filename
 * @param {Function} onProgress - Callback for progress (0-100)
 */
async function downloadWithProgress(url, filename, onProgress) {
  const platform = detectPlatform();

  try {
    // Report initial progress
    if (onProgress) onProgress(0);

    // Fetch the file
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Get content length for progress calculation
    const contentLength = response.headers.get('content-length');
    const total = parseInt(contentLength, 10);

    // Read the response as a stream
    const reader = response.body.getReader();
    const chunks = [];
    let receivedLength = 0;

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      chunks.push(value);
      receivedLength += value.length;

      // Report progress
      if (onProgress && total) {
        const percent = Math.round((receivedLength / total) * 100);
        onProgress(percent);
      }
    }

    // Combine chunks into single blob
    const blob = new Blob(chunks);

    // Create download link
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.style.display = 'none';

    // Append to body (required for Firefox)
    document.body.appendChild(link);

    // Trigger download
    link.click();

    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }, 100);

    // Report complete
    if (onProgress) onProgress(100);

    return {
      success: true,
      platform: platform,
      message: platform === 'android'
        ? 'Download complete. Check your Downloads folder.'
        : 'Download complete. Check your browser downloads.'
    };

  } catch (error) {
    console.error('Download with progress error:', error);

    // Fallback to simple download without progress
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => document.body.removeChild(link), 100);

    return {
      success: false,
      platform: platform,
      message: 'Download started (progress tracking unavailable)',
      error: error.message
    };
  }
}

/**
 * Get platform-specific download instructions
 */
export const getDownloadInstructions = (platform = null) => {
  const detectedPlatform = platform || detectPlatform();

  const instructions = {
    ios: {
      title: 'To save to your device:',
      steps: [
        'Tap the button above to open the file',
        'Tap the Share icon (□↑) in the top right',
        'Select "Save to Files"',
        'Choose your save location'
      ]
    },
    android: {
      title: 'To save to your device:',
      steps: [
        'Tap the Download button above',
        'Check your notification for download progress',
        'Find the file in your Downloads folder'
      ]
    },
    desktop: {
      title: 'To save to your device:',
      steps: [
        'Click the Download button above',
        'File will be saved to your default downloads folder'
      ]
    }
  };

  return instructions[detectedPlatform] || instructions.desktop;
};

/**
 * Get platform-specific button label
 */
export const getDownloadButtonLabel = (fileType, platform = null) => {
  const detectedPlatform = platform || detectPlatform();

  const typeLabel = {
    video: 'Video',
    document: 'Document',
    presentation: 'Presentation (PPT)',
    file: 'File'
  }[fileType] || 'File';

  if (detectedPlatform === 'ios') {
    return `Open ${typeLabel}`;
  }

  return `Download ${typeLabel}`;
};
